import {Box} from '@dropbox/dig-foundations';
import {PersonHoverCard, TeamHoverCard} from 'components/DSYS/HoverCard';
import {Link} from 'components/DSYS/Link';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversionMap,
  type DOMConversionOutput,
  DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {simpleHash} from 'plugins/utils';
import {ElementType, ReactNode} from 'react';

export const SHOW_PROFILE = false;

export type SerializedMentionNode = Spread<
  {
    trigger: string;
    value: string;
    mentionType: 'employee' | 'team';
    id: string;
    mentionNodeId: string;
  },
  SerializedLexicalNode
>;

function convertElement(domNode: HTMLElement): DOMConversionOutput | null {
  const trigger = domNode.getAttribute('data-lexical-mention-trigger');
  const value = domNode.getAttribute('data-lexical-mention-value');
  const mentionType = domNode.getAttribute('data-lexical-mention-type');
  const id = domNode.getAttribute('data-lexical-mention-id');
  const mentionNodeId =
    domNode.getAttribute('data-lexical-mention-node-id') || generateMentionNodeId();
  if (
    trigger != null &&
    value !== null &&
    mentionType !== null &&
    id !== null &&
    mentionNodeId !== null
  ) {
    const node = $createMentionNode(
      trigger,
      value,
      mentionType as 'employee' | 'team',
      id,
      mentionNodeId
    );
    return {node};
  }
  return null;
}

/**
 * Generate a unique mention ID
 * Format: mention-{shortHash}
 */
export function generateMentionNodeId(): string {
  return `mention-${simpleHash()}`;
}

/**
 * This node is used to represent a mention used in the MentionPlugin.
 */
export class MentionNode extends DecoratorNode<ReactNode> {
  __trigger: string;
  __value: string;
  __mentionType: 'employee' | 'team';
  __id: string;
  __mentionNodeId: string;

  static getType(): string {
    return 'Mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(
      node.__trigger,
      node.__value,
      node.__mentionType,
      node.__id,
      node.__mentionNodeId,
      node.__key
    );
  }

  constructor(
    trigger: string,
    value: string,
    mentionType: 'employee' | 'team',
    id: string,
    mentionNodeId?: string,
    key?: NodeKey
  ) {
    super(key);
    this.__trigger = trigger;
    this.__value = value;
    this.__mentionType = mentionType;
    this.__id = id;
    this.__mentionNodeId = mentionNodeId || generateMentionNodeId();
  }

  createDOM(): HTMLElement {
    return document.createElement('span');
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', 'true');
    element.setAttribute('data-lexical-mention-trigger', this.__trigger);
    element.setAttribute('data-lexical-mention-value', this.__value);
    element.setAttribute('data-lexical-mention-type', this.__mentionType);
    element.setAttribute('data-lexical-mention-id', this.__id);
    element.setAttribute('data-lexical-mention-node-id', this.__mentionNodeId);
    element.textContent = this.getTextContent();
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null;
        }
        return {
          conversion: convertElement,
          priority: 1,
        };
      },
    };
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const mentionNodeId = serializedNode.mentionNodeId || generateMentionNodeId();
    return $createMentionNode(
      serializedNode.trigger,
      serializedNode.value,
      serializedNode.mentionType,
      serializedNode.id,
      mentionNodeId
    );
  }

  exportJSON(): SerializedMentionNode {
    return {
      trigger: this.__trigger,
      value: this.__value,
      mentionType: this.__mentionType,
      id: this.__id,
      mentionNodeId: this.__mentionNodeId,
      type: 'Mention',
      version: 1,
    };
  }

  getMentionNodeId(): string {
    return this.__mentionNodeId;
  }

  getTextContent(): string {
    const self = this.getLatest();
    return self.__trigger + self.__value;
  }

  getTrigger(): string {
    const self = this.getLatest();
    return self.__trigger;
  }

  getValue(): string {
    const self = this.getLatest();
    return self.__value;
  }

  setValue(value: string) {
    const self = this.getWritable();
    self.__value = value;
  }

  component(): ElementType<any> | null {
    return null;
  }

  decorate() {
    const name = this.getTrigger();
    const slug = this.getValue();
    const linkPath = `/${this.__mentionType === 'team' ? 'teams' : 'people'}/${slug}`;

    if (!SHOW_PROFILE) {
      const mentionLink = (
        <Link
          to={linkPath}
          hasNoUnderline
          target="_blank"
          data-mention-node-id={this.__mentionNodeId}
          style={{fontSize: 'inherit'}}
        >
          @{name}
        </Link>
      );

      return this.__mentionType === 'employee' ? (
        <PersonHoverCard ldap={slug} name={name}>
          {mentionLink}
        </PersonHoverCard>
      ) : (
        <TeamHoverCard slug={slug} name={name}>
          {mentionLink}
        </TeamHoverCard>
      );
    }

    return (
      <Link to={linkPath} hasNoUnderline data-mention-node-id={this.__mentionNodeId}>
        <OptionalTooltip title={this.getValue()} enabled={this.__mentionType === 'team'}>
          <Box
            display="inline-flex"
            backgroundColor="Opacity Surface"
            borderRadius="Small"
            position="relative"
            paddingX="4"
            style={{top: 3}}
          >
            <Box style={{marginTop: 1}}>{this.getTrigger()}</Box>
          </Box>
        </OptionalTooltip>
      </Link>
    );
  }
}

export function $createMentionNode(
  trigger: string,
  value: string,
  mentionType: 'employee' | 'team',
  id: string,
  mentionNodeId?: string
): MentionNode {
  const mentionNode = new MentionNode(trigger, value, mentionType, id, mentionNodeId);
  return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
